import React, { useState, useEffect } from "react";
import { Box } from "@mui/system";
import { Grid, Button } from "@mui/material";
import {
  TextInput,
  SelectInput,
  ReferenceInput,
  required,
  maxLength,
  useDataProvider,
  useNotify,
  useRefresh,
  NumberInput,
  Form,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import ResetButton from "../ResetButton";
import LocationRow from "./LocationRow";
import TravelersRow from "./TravelersRow";
import DomesticTripCategoryInput from "./DomesticTripCategoryInput";
import PersonalCarFields from "./PersonalCarFields";
import BusinessTripRequestPreview from "./BusinessTripRequestPreview";
import { BusinessTripMealProvidedChoices } from "utils/choices";
import { employeeRequests } from "api/employeeApi";
import { getInclusiveDayCount } from "utils/helpers";
import { validateEnd, validateStart, validateDateInAMonth } from "components/Leaves/Requests/utils/validations";
import { toBusinessTripPayload } from "components/Leaves/Requests/utils/businessTrip";
import { useNotifyError } from "utils/useNotifyError";
import { BusinessTripTypes } from "utils/constants";

const AutoDailyAllowanceDaysAndNights = () => {
  const { setValue } = useFormContext();
  const dateFrom = useWatch({ name: "dateFrom" });
  const dateTo = useWatch({ name: "dateTo" });

  useEffect(() => {
    const days = getInclusiveDayCount(dateFrom, dateTo);
    if (days == null) return;
    setValue("dailyAllowanceDays", days, { shouldValidate: true, shouldDirty: true });
    setValue("nightsCount", Math.max(days - 1, 0), { shouldValidate: true, shouldDirty: true });
  }, [dateFrom, dateTo, setValue]);

  return null;
};

const PreviewButton = ({ onPreview }) => {
  const { trigger, getValues } = useFormContext();

  const handleClick = async () => {
    const isValid = await trigger();
    if (!isValid) return;
    onPreview(getValues());
  };

  return (
    <Button
      variant="contained"
      color="success"
      type="button"
      onClick={handleClick}
      sx={{ padding: "0.5rem", whiteSpace: "nowrap" }}
    >
      Преглед на заявка
    </Button>
  );
};

const DateRangeRow = () => {
  const dateFrom = useWatch({ name: "dateFrom" });
  const dateTo = useWatch({ name: "dateTo" });

  return (
    <Grid
      container
      item
      xs={12}
      md={6}
      lg={6}
    >
      <Grid
        item
        xs={6}
        p={0.5}
      >
        <CustomDateInput
          label="От дата"
          source="dateFrom"
          noTime
          required={true}
          validate={[
            () =>
              validateStart(
                dateFrom,
                dateTo,
                required,
                "Крайният ден не може да бъде по-малък от началният",
              ),
            () => validateDateInAMonth(dateFrom),
          ]}
        />
      </Grid>
      <Grid
        item
        xs={6}
        p={0.5}
      >
        <CustomDateInput
          label="До дата"
          source="dateTo"
          noTime
          required={true}
          minDate={dateFrom}
          validate={() =>
            validateEnd(
              dateTo,
              dateFrom,
              required,
              "Крайният ден не може да бъде по-малък от началният",
            )
          }
        />
      </Grid>
    </Grid>
  );
};

const BusinessTripFields = ({ onPreview, setLeaveId }) => {
  const businessTripType = useWatch({ name: "businessTripType" });
  const domesticTripCategory = useWatch({ name: "domesticTripCategory" });
  const isDomestic = businessTripType === BusinessTripTypes.Internal;

  if (!businessTripType) return null;
  if (isDomestic && !domesticTripCategory) return null;

  return (
    <Grid
      container
      spacing={0}
    >
      <Grid
        item
        xs={6}
        lg={5}
        p={0.5}
      >
        <ReferenceInput
          source="transportType"
          reference={employeeRequests.getLeaveTransportsSelect()}
        >
          <SelectInput
            label="Тип транспорт"
            optionText="nameBg"
            validate={required()}
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
          />
        </ReferenceInput>
      </Grid>
      <PersonalCarFields />
      <DateRangeRow />
      <TravelersRow />
      <Grid container>
        <LocationRow />
        <Grid
          item
          xs={12}
          md={isDomestic ? 12 : 6}
          p={0.5}
        >
          <TextInput
            sx={{ mt: 3, mb: 0 }}
            label="По маршрут"
            source="route"
            multiline
            minRows={9}
            validate={required()}
          />
        </Grid>
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        lg={4}
        p={0.5}
      >
        <SelectInput
          label="Осигурено изхранване"
          source="mealProvided"
          choices={BusinessTripMealProvidedChoices}
          validate={required()}
          SelectProps={{ MenuProps: { disableScrollLock: true } }}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        lg={4}
        p={0.5}
      >
        <NumberInput
          label="Дневни пари на ден"
          source="dailyAllowancePerDay"
          min={0}
          validate={required()}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        lg={4}
        p={0.5}
      >
        <NumberInput
          label="Брой дни с дневни пари"
          source="dailyAllowanceDays"
          min={0}
          readOnly
          validate={required()}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        lg={4}
        p={0.5}
      >
        <NumberInput
          label="Макс. сума за нощувка"
          source="maxAccommodationAmountPerNight"
          min={0}
          validate={required()}
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={4}
        lg={4}
        p={0.5}
      >
        <NumberInput
          label="Брой нощувки"
          source="nightsCount"
          min={0}
          readOnly
          validate={required()}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        p={0.5}
      >
        <TextInput
          type="textarea"
          label="Платец на разходите"
          source="expensePayer"
          multiline
          minRows={4}
          validate={[required(), maxLength(300)]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        lg={6}
        p={0.5}
      >
        <TextInput
          type="textarea"
          label={isDomestic ? "Задача" : "Причина за командировката"}
          source="assignment"
          multiline
          minRows={4}
          validate={[required(), maxLength(500)]}
        />
      </Grid>
      <Grid
        container
        justifyContent="space-between"
        p={0.5}
      >
        <Grid item>
          <ResetButton onReset={() => setLeaveId("")} />
        </Grid>
        <Grid item>
          <PreviewButton onPreview={onPreview} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const BusinessTripRequest = ({ onSuccess, showPreview, setShowPreview, setLeaveId }) => {
  const dataProvider = useDataProvider();
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();
  const [previewData, setPreviewData] = useState(null);
  const handlePreview = values => {
    setPreviewData(toBusinessTripPayload(values));
    setShowPreview(true);
  };

  const handleConfirm = async () => {
    try {
      await dataProvider.customRequest(employeeRequests.createBusinessTrip(), previewData, "POST");
      notify("Заявката беше регистрирана! Ще получите имейл нотификация при одобрението ѝ.", { type: "success" });
      setShowPreview(false);
      setPreviewData(null);
      onSuccess();
      refresh();
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <Form>
      <AutoDailyAllowanceDaysAndNights />
      {showPreview ? (
        <BusinessTripRequestPreview
          data={previewData}
          onBack={() => setShowPreview(false)}
          onConfirm={handleConfirm}
        />
      ) : (
        <Box sx={{ pt: 5 }}>
          <Grid
            container
            spacing={0}
          >
            <Grid
              item
              xs={12}
              md={6}
              lg={4}
            >
              <ReferenceInput
                source="businessTripType"
                reference={employeeRequests.getBusinessTripsSelect()}
              >
                <SelectInput
                  fullWidth
                  label="Тип командировка"
                  optionText="nameBg"
                  validate={required()}
                  SelectProps={{ MenuProps: { disableScrollLock: true } }}
                />
              </ReferenceInput>
            </Grid>
            <DomesticTripCategoryInput />
          </Grid>
          <BusinessTripFields
            onPreview={handlePreview}
            setLeaveId={setLeaveId}
          />
        </Box>
      )}
    </Form>
  );
};

export default BusinessTripRequest;
